/** User endpoints: auth, profiles, roles, account administration. */

import apiBase, { BaseQueryParams, PaginatedResponse, publicApi } from './api-base';
import { TokenManager } from '@/lib/token-manager';
import { AxiosError } from 'axios';

/**
 * Type definitions for User module
 */
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  phone_number?: string;
  address?: string;
  date_joined: string;
  last_login?: string;
  user_type?: 'student' | 'staff' | 'faculty' | 'librarian' | 'admin';
  // Extended authZ claims from backend current_user endpoint
  roles?: string[]; // Multiple active role assignments
  permissions?: string[]; // Aggregated permission codes
  is_locked_out?: boolean; // Locked after repeated failed sign-ins (login lock or axes)
  student_id?: string;
  employee_id?: string;
  date_of_birth?: string;
  is_active_member?: boolean;
  membership_expiry?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

export interface UserProfile {
  id: number;
  user: User;
  library_card_number: string;
  department?: string;
  phone_number?: string;
  address?: string;
  date_of_birth?: string;
  emergency_contact?: string;
  created_at: string;
  updated_at: string;
}

/** One signed-in device, from GET /auth/sessions/. */
export interface DeviceSession {
  id: number;
  device: string;
  ip_address: string | null;
  user_agent: string;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
  /** TOTP or backup code, sent on the second step when the first answered 202. */
  two_fa_token?: string;
}

export interface LoginResponse {
  token?: string;
  user: User;
  expires_in?: number;
  expires_at?: string;
  auth_mode?: 'cookie';
}

/** POST /auth/login/ answered 202: credentials accepted, second factor pending. */
export class TwoFactorRequiredError extends Error {
  readonly userId: number;

  constructor(userId: number) {
    super('Two-factor authentication required');
    this.name = 'TwoFactorRequiredError';
    this.userId = userId;
  }
}

export interface CreateUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
  user_type?: string;
  is_active?: boolean;
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  address?: string;
  date_of_birth?: string;
  is_active_member?: boolean;
  membership_expiry?: string;
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  user_type?: string;
  phone_number?: string;
  address?: string;
  student_id?: string;
  employee_id?: string;
  /** null clears the date on update. */
  date_of_birth?: string | null;
  is_active_member?: boolean;
  membership_expiry?: string | null;
}

export interface UserQueryParams extends BaseQueryParams {
  user_type?: string;
  is_active?: boolean;
}

/** Account-wide counts for the staff Users page (`users/summary/`, staff tier only). */
export interface UserSummary {
  total: number;
  active: number;
  /** Inactive accounts: self-registrations awaiting approval and deactivated ones. */
  pending: number;
  new_this_week: number;
  /** Librarian and admin accounts. */
  staff: number;
}

/**
 * Authentication API endpoints
 */
export const authApi = {
  /**
   * Sign in
   * @param credentials - Login credentials
   * @returns Promise with user data and token
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await apiBase.post('/auth/login/', credentials, {
        headers: { 'X-Use-Cookie-Auth': 'true' },
      });

      if (response.status === 202) {
        throw new TwoFactorRequiredError(response.data.user_id);
      }

      // Extract token and basic info from login response
      const { token, expires_in, expires_at } = response.data;
      
      // Calculate expiration time from backend response (default to 1 hour if not provided)
      const expiresInSeconds = expires_in || 3600; // 1 hour default
      const expiresAt = expires_at ? new Date(expires_at) : new Date();
      if (!expires_at) {
        expiresAt.setSeconds(expiresAt.getSeconds() + expiresInSeconds);
      }
      
      // Store token first so subsequent API calls are authenticated
      TokenManager.setTokens({
        token,
        expiresAt: expiresAt.toISOString(),
        user: null as any, // Temporary, will be replaced immediately
      });
      
      // Fetch complete user data with all permissions and roles from /auth/user/
      // This ensures we get is_staff, is_superuser, roles, permissions, etc.
      const userResponse = await apiBase.get('/auth/user/');
      const user = userResponse.data;
      
      // Update stored user data with complete information
      TokenManager.updateUser(user);
      
      console.log('Login successful, complete user data fetched:', user);
      
      return { token, user, expires_in, expires_at, auth_mode: response.data.auth_mode };
    } catch (error: unknown) {
      // Record the failed attempt
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as Record<string, unknown>;
      const errorData = {
        timestamp: new Date().toISOString(),
        username: credentials.username,
        ip_address: 'client', // Would be better to get from server
        error_message: responseData?.detail || 'Login failed'
      };
      
      console.warn('Login attempt failed:', errorData);
      
      // Clean up token if we stored it but failed to get user data
      TokenManager.clearTokens();
      throw error;
    }
  },

  /**
   * User logout with token cleanup
   * @returns Promise with logout confirmation
   */
  logout: async () => {
    try {
      await apiBase.post('/auth/logout/');
    } catch (error) {
      // Continue with logout even if server request fails
      console.warn('Server logout failed, cleaning up locally:', error);
    } finally {
      // Always clean up local tokens
      TokenManager.clearTokens();
    }
  },

  /**
   * Change user password
   * @param data - Password change data
   * @returns Promise with new token
   */
  changePassword: async (data: {
    old_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ message: string; auth_mode?: 'cookie'; sessions_signed_out: number }> => {
    const response = await apiBase.post('/auth/password/change/', data);
    // This device keeps its token; every other device is signed out
    // (`sessions_signed_out`). Only the local expiry hint needs refreshing.
    TokenManager.updateToken(undefined, 60 * 60 * 1000);
    return response.data;
  },

  /**
   * Request password reset
   * @param email - User email
   * @returns Promise with reset confirmation
   */
  requestPasswordReset: async (email: string): Promise<{ message: string }> => {
    const response = await publicApi.post('/auth/password/reset/', { email });
    return response.data;
  },

  /**
   * Complete a password reset from the e-mailed link (uid + token).
   */
  confirmPasswordReset: async (data: {
    uid: string;
    token: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ message: string }> => {
    const response = await publicApi.post('/auth/password/reset/confirm/', data);
    return response.data;
  },

  /**
   * Signed-in devices for the current account.
   *
   * Each row is one device token (backend `users.models.AuthToken`). Ending a
   * session deletes that credential, so the device's next request is a 401.
   */
  listSessions: async (): Promise<{ count: number; sessions: DeviceSession[] }> => {
    const response = await apiBase.get('/auth/sessions/');
    return response.data;
  },

  /** End one device's session. Ending the current one signs this browser out. */
  endSession: async (sessionId: number): Promise<{ message: string; was_current_session: boolean }> => {
    const response = await apiBase.post('/auth/sessions/terminate/', { session_id: sessionId });
    return response.data;
  },

  /** End every session except this browser's. */
  endOtherSessions: async (): Promise<{ message: string; terminated_count: number }> => {
    const response = await apiBase.post('/auth/sessions/terminate-others/', {});
    return response.data;
  },

  /**
   * Get current user permissions
   * @returns Promise with user permissions and roles
   */
  getPermissions: async (): Promise<{ permissions: string[]; roles: string[]; is_librarian_or_above: boolean }> => {
    const response = await apiBase.get('/auth/permissions/');
    return response.data;
  },

  /**
   * Refresh authentication token
   * @param refreshToken - Refresh token
   * @returns Promise with new token and refresh token
   */
  refreshToken: async (_refreshToken?: string): Promise<{
    token?: string;
    refresh_token?: string;
    expires_in?: number;
  }> => {
    const response = await apiBase.post('/auth/token/refresh/', {});
    return response.data;
  },

  /**
   * Get current authenticated user data
   * @returns Promise with current user data
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiBase.get('/auth/user/');
    return response.data;
  },
};

/**
 * User Management API endpoints
 */
export const usersApi = {
  /**
   * Get paginated list of users with filtering
   * @param params - Query parameters
   * @returns Promise with paginated user list
   */
  getUsers: async (params?: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const response = await apiBase.get('/users/', { params });
    return response.data;
  },

  /**
   * Account-wide counts for the KPI tiles (never the current page's rows)
   */
  getUserSummary: async (): Promise<UserSummary> => {
    const response = await apiBase.get('/users/summary/');
    return response.data;
  },

  /**
   * Get specific user by ID
   * @param id - User ID
   * @returns Promise with user data
   */
  getUser: async (id: number): Promise<User> => {
    const response = await apiBase.get(`/users/${id}/`);
    return response.data;
  },

  /**
   * Create new user
   * @param userData - User creation data
   * @returns Promise with created user
   */
  createUser: async (userData: CreateUserData): Promise<User> => {
    // Filter to only send fields that the backend serializer expects
    const allowedFields = [
      'username',
      'email',
      'first_name',
      'last_name',
      'password',
      'confirm_password',
      'user_type',
      'is_active',
      'student_id',
      'employee_id',
      'phone_number',
      'date_of_birth',
      'address'
    ];
    
    const filteredData = Object.fromEntries(
      Object.entries(userData).filter(([key]) => allowedFields.includes(key))
    );
    
    try {
      const response = await apiBase.post('/users/', filteredData);
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 400) {
        const errorData = axiosError.response?.data as any;
        console.error('User creation validation errors:', errorData);
      }
      throw error;
    }
  },

  /**
   * Update existing user
   * @param id - User ID
   * @param userData - User update data
   * @returns Promise with updated user
   */
  updateUser: async (id: number, userData: UpdateUserData): Promise<User> => {
    const response = await apiBase.put(`/users/${id}/`, userData);
    return response.data;
  },

  /**
   * Partially update user
   * @param id - User ID
   * @param userData - Partial user data
   * @returns Promise with updated user
   */
  patchUser: async (id: number, userData: Partial<UpdateUserData>): Promise<User> => {
    const response = await apiBase.patch(`/users/${id}/`, userData);
    return response.data;
  },

  /**
   * Search users with query
   * @param query - Search query
   * @param params - Additional parameters
   * @returns Promise with search results
   */
  searchUsers: async (query: string, params?: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const searchParams = { query, ...params };
    const response = await apiBase.get('/users/search/', { params: searchParams });
    return response.data;
  },

  /**
   * Reset user password (Admin only)
   * @param id - User ID
   * @returns Promise with temporary password
   */
  resetUserPassword: async (id: number): Promise<{ message: string; delivered_to: string }> => {
    const response = await apiBase.post(`/users/${id}/reset_password/`);
    return response.data;
  },

  /**
   * Unlock user account (Admin only)
   * @param id - User ID
   * @returns Promise with success message
   */
  unlockUserAccount: async (id: number): Promise<{ detail: string }> => {
    const response = await apiBase.post(`/users/${id}/unlock_account/`);
    return response.data;
  },

  /**
   * Delete user (Admin/Librarian only)
   * @param id - User ID
   * @returns Promise with success message
   */
  deleteUser: async (id: number): Promise<{ detail: string }> => {
    const response = await apiBase.delete(`/users/${id}/`);
    return response.data;
  },
};

/**
 * User Profile API endpoints
 */
export const profilesApi = {
  /**
   * Get paginated list of profiles
   * @param params - Query parameters
   * @returns Promise with paginated profile list
   */
  getProfiles: async (params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>> => {
    const response = await apiBase.get('/profiles/', { params });
    return response.data;
  },

  /**
   * Get current user's profile
   * @returns Promise with user profile
   */
  getMyProfile: async (): Promise<UserProfile> => {
    const response = await apiBase.get('/profiles/my_profile/');
    return response.data;
  },

  /**
   * Update current user's profile
   * @param profileData - Profile update data
   * @returns Promise with updated profile
   */
  updateMyProfile: async (profileData: Partial<UserProfile>): Promise<UserProfile> => {
  const response = await apiBase.patch('/profiles/update_my_profile/', profileData);
    return response.data;
  },

  /**
   * Upload or update current user's avatar image
   * Accepts a FormData object containing the file under key `avatar`.
   * Uses multipart/form-data so the server can store the file.
   */
  uploadMyAvatar: async (formData: FormData): Promise<UserProfile> => {
  const response = await apiBase.patch('/profiles/update_my_profile/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Get specific profile by ID
   * @param id - Profile ID
   * @returns Promise with profile data
   */
  getProfile: async (id: number): Promise<UserProfile> => {
    const response = await apiBase.get(`/profiles/${id}/`);
    return response.data;
  },
};

/**
 * User Roles and Permissions API endpoints
 */
export const rolesApi = {
  /**
   * Get user roles
   * @param params - Query parameters
   * @returns Promise with roles data
   */
  getRoles: async (params?: BaseQueryParams) => {
    const response = await apiBase.get('/roles/', { params });
    return response.data;
  },

  /**
   * Assign role to user
   * @param roleData - Role assignment data
   * @returns Promise with assignment result
   */
  assignRole: async (roleData: { user_id: number; role: string }) => {
    const response = await apiBase.post('/roles/', roleData);
    return response.data;
  },

  /**
   * Update user role
   * @param id - Role assignment ID
   * @param roleData - Updated role data
   * @returns Promise with updated role assignment
   */
  updateRole: async (id: number, roleData: Partial<{ role: string; is_active: boolean }>) => {
    const response = await apiBase.patch(`/roles/${id}/`, roleData);
    return response.data;
  },
};

export interface PublicRegistrationData {
  role: 'student' | 'teacher';
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  password: string;
  confirm_password: string;
  // Student only
  school_id?: string;
  year_group?: number;
  reg_group?: string;
}

export const registerUser = async (data: PublicRegistrationData): Promise<void> => {
  await publicApi.post('/auth/register/', data);
};
